import { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import { FlowForm, FlowFormLeaveContext, List, ListItem, ButtonBar, ButtonBarGroup, useFlowFormGlobal } from 't-lib-ui/structure';
import { TextButton, InfiniteLoader, Note, Input } from 't-lib-ui/widgets';
import { debounce1 } from 't-lib-ui/helpers';
import { refreshIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { useSearchCatalog, useGetCatalogByCategory } from '../../connector/api';
import { RestCatalog } from '../../schema-copy/api-schema';
import { QueryStatus } from 't-lib-ui/connector';
import { formatCompactDate } from '../../utils/formatting';
import { usePreferences } from '../../connector/preferences';

const CATALOGS_TO_SHOW = 25;

interface RenderCatalogProps {
  catalog: RestCatalog;
}

const RenderCatalog: React.FC<RenderCatalogProps> = ({ catalog }) => {
  const statusColor = catalog.feedStatus == 'ACTIVE' ? 'var(--success-color)' : 'var(--error-color)';
  const statusText = catalog.feedStatus;
  const lastPostDate = formatCompactDate(catalog.lastNewPostPubDate);
  const createdDate = formatCompactDate(catalog.dateAdded);

  // Determine icon based on catalog status
  const iconColor = catalog.feedStatus == 'ACTIVE' ? 'var(--dr-link-blue)' : 'var(--error-color)';

  const renderIcon = () => {
    // Globe icon for published catalogs
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    );
  };

  return (
    <ListItem focussable="flow" href={`/a/admin/catalog/${catalog.id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {renderIcon()}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500 }}>{catalog.title || catalog.shortTitle || 'Untitled Feed'}</div>
          <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
            <span style={{ color: statusColor, whiteSpace: 'nowrap' }}>
              {statusText}
              {catalog.errorType && ` (${catalog.errorType})`}
            </span>
            <span style={{ whiteSpace: 'nowrap' }}>•</span>
            <span style={{ whiteSpace: 'nowrap' }}>{catalog.catalogSubscriberCount || 0} sub{catalog.catalogSubscriberCount == 1 ? '' : 's'}</span>
            <span style={{ whiteSpace: 'nowrap' }}>•</span>
            <span style={{ whiteSpace: 'nowrap' }}>promo: {catalog.promotion}</span>
            {catalog.category && (
              <>
                <span style={{ whiteSpace: 'nowrap' }}>•</span>
                <span style={{ whiteSpace: 'nowrap' }}>{catalog.category}</span>
              </>
            )}
            {lastPostDate && (
              <>
                <span style={{ whiteSpace: 'nowrap' }}>•</span>
                <span style={{ whiteSpace: 'nowrap' }}>last post: {lastPostDate}</span>
              </>
            )}
            {createdDate && (
              <>
                <span style={{ whiteSpace: 'nowrap' }}>•</span>
                <span style={{ whiteSpace: 'nowrap' }}>added: {createdDate}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </ListItem>
  );
};

const AdminCatalogsPage: React.FC = () => {
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [preferences, updatePreferences] = usePreferences();
  const initialSearch = preferences.adminCatalogSearchName || '';
  const [searchName, setSearchName] = useState<string>(initialSearch);
  const [searchActive, setSearchActive] = useState<boolean>(!!initialSearch.trim());

  useFlowFormGlobal();

  // Load preferences on mount
  useEffect(() => {
    if (preferences.adminCatalogSearchName) {
      setSearchName(preferences.adminCatalogSearchName);
    }
  }, [preferences.adminCatalogSearchName]);

  // Use category-based query when no search term, search query when there is a search term
  const hasSearchTerm = !!searchName.trim();

  const categoryQuery = useGetCatalogByCategory(
    CATALOGS_TO_SHOW,
    undefined,
    { enabled: !!adminSecret && !searchActive }
  );

  const searchQuery = useSearchCatalog(
    CATALOGS_TO_SHOW,
    searchName,
    { enabled: !!adminSecret && hasSearchTerm && searchActive }
  );

  // Use whichever query is active
  const { chunks, status, hasMore, loadMore, refresh } = searchActive ? searchQuery : categoryQuery;

  const catalogCount = useMemo(
    () => chunks?.reduce((sum, chunk) => sum + chunk.items.length, 0),
    [chunks]
  );

  const debouncedSearch = useMemo(
    () => debounce1(1, (value: string) => {
      if (value.trim()) {
        setSearchActive(true);
        searchQuery.changeInput({ query: value });
      }
    }, 600),
    [searchQuery]
  );

  const handleSearchChange = (value: string) => {
    setSearchName(value);
    updatePreferences({ adminCatalogSearchName: value });
    if (!value.trim()) {
      setSearchActive(false);
    }
    debouncedSearch(value);
  };

  const successful = status == QueryStatus.Success && chunks;

  function RenderChunk({ chunk }: { chunk: RestCatalog[] }) {
    return chunk.map((catalog) => <RenderCatalog key={catalog.id} catalog={catalog} />);
  }

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-catalogs"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              <AdminHeader title="Feed Catalog" onExpand={onExpand} />
              <ButtonBar size="small">
                <TextButton
                  label="Refresh"
                  className="refresh"
                  onClick={() => refresh(true)}
                  focussable="flow"
                  icon={refreshIcon}
                />
                <ButtonBarGroup groupName="Search:">
                  <Input
                    type="text"
                    placeholder="Search catalog..."
                    value={searchName}
                    onInput={handleSearchChange}
                    borderless={false}
                    layout="singleCol"
                  />
                </ButtonBarGroup>
              </ButtonBar>

              <List>
                {chunks && chunks.flatMap((chunk) => RenderChunk({ chunk: chunk.items }))}
                {!hasMore && !!catalogCount && (
                  <Note type="tiny">
                    Found {catalogCount} catalog{catalogCount == 1 ? '' : 's'}.
                  </Note>
                )}
                {successful && !chunks?.[0].items?.length && (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                    No catalogs found.
                  </div>
                )}
                {status == QueryStatus.Error && (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--error-color)' }}>
                    Error loading catalogs!
                  </div>
                )}
                <div style={{ textAlign: 'center' }}>
                  <InfiniteLoader active={hasMore} onActivation={loadMore} />
                </div>
              </List>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminCatalogsPage;
